/**
 * Utilidad para manejo consistente de errores en toda la aplicación
 */

/**
 * Obtiene un mensaje de error específico basado en el código de estado HTTP
 * @param {Object} error - Error object de axios
 * @param {string} defaultMessage - Mensaje por defecto si no se puede determinar el error
 * @returns {string} Mensaje de error específico para el usuario
 */
export const getErrorMessage = (error, defaultMessage = "Ha ocurrido un error inesperado") => {
  console.error('🔍 Error details:', error);
  console.error('🔍 Error response:', error.response);
  console.error('🔍 Error status:', error.response?.status);
  console.error('🔍 Error data:', error.response?.data);

  // Si hay un mensaje específico del backend, usarlo
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Mensajes específicos por código de estado HTTP
  switch (error.response?.status) {
    case 400:
      return "Datos inválidos. Por favor, verifica la información ingresada.";
    case 401:
      return "Sesión expirada. Por favor, inicia sesión nuevamente.";
    case 403:
      return "No tienes permisos para realizar esta acción.";
    case 404:
      return "No se encontró el recurso solicitado. Por favor, recarga la página.";
    case 409:
      return "Conflicto de datos. El recurso ya existe o está en uso.";
    case 422:
      return "Datos de validación incorrectos. Por favor, revisa los campos marcados.";
    case 429:
      return "Demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente.";
    case 500:
      return "Error del servidor. Por favor, intenta nuevamente más tarde.";
    case 502:
      return "Servicio temporalmente no disponible. Por favor, intenta más tarde.";
    case 503:
      return "Servicio en mantenimiento. Por favor, intenta más tarde.";
    default:
      // Si hay un mensaje de error genérico, usarlo
      if (error.message) {
        return error.message;
      }
      return defaultMessage;
  }
};

/**
 * Maneja errores de forma consistente con logging y toast
 * @param {Object} error - Error object
 * @param {Function} toast - Función toast para mostrar mensajes
 * @param {string} defaultMessage - Mensaje por defecto
 * @param {string} context - Contexto donde ocurrió el error (para logging)
 */
export const handleError = (error, toast, defaultMessage, context = '') => {
  const errorMessage = getErrorMessage(error, defaultMessage);
  
  if (context) {
    console.error(`🔍 Error en ${context}:`, error);
  }
  
  toast("error", errorMessage);
};

/**
 * Maneja errores de forma consistente con logging, toast y callback opcional
 * @param {Object} error - Error object
 * @param {Function} toast - Función toast para mostrar mensajes
 * @param {string} defaultMessage - Mensaje por defecto
 * @param {string} context - Contexto donde ocurrió el error (para logging)
 * @param {Function} onError - Callback opcional para manejo adicional del error
 */
export const handleErrorWithCallback = (error, toast, defaultMessage, context = '', onError = null) => {
  const errorMessage = getErrorMessage(error, defaultMessage);
  
  if (context) {
    console.error(`🔍 Error en ${context}:`, error);
  }
  
  toast("error", errorMessage);
  
  if (onError && typeof onError === 'function') {
    onError(error, errorMessage);
  }
};

/**
 * Wrapper para funciones async que maneja errores automáticamente
 * @param {Function} asyncFn - Función async a ejecutar
 * @param {Function} toast - Función toast para mostrar mensajes
 * @param {string} defaultMessage - Mensaje por defecto
 * @param {string} context - Contexto donde ocurrió el error (para logging)
 * @param {Function} onError - Callback opcional para manejo adicional del error
 * @returns {Promise} Promise que resuelve con el resultado o rechaza con error manejado
 */
export const withErrorHandling = (asyncFn, toast, defaultMessage, context = '', onError = null) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleErrorWithCallback(error, toast, defaultMessage, context, onError);
      throw error; // Re-lanzar para que el componente pueda manejar si es necesario
    }
  };
};
